/**
 * Full-screen "Trial ended — choose a plan" lockout.
 *
 * Renders inside :file:`AppShell.tsx` whenever the resolved features
 * bundle has ``is_trial_expired === true`` AND the user is NOT already
 * on a billing/settings route. The intent is to block the rest of the
 * product so the admin can complete checkout without distraction.
 *
 * Why an in-shell lockout rather than a route redirect:
 *   • The sidebar/top bar still render, but every nav item is a no-op
 *     because the route guards (module gate) already 403 anything
 *     outside ``billing`` / ``settings`` for trial_expired tenants.
 *   • Putting the screen inside the shell keeps the brand chrome
 *     visible, which is calmer than a sudden full-page redirect.
 *   • Employees never see this — the audience gate in TrialBanner is
 *     SUPER_ADMIN-only and so is the path to checkout. For
 *     non-admin roles we render a softer "Your workspace is paused"
 *     message that points them at their admin.
 */
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Lock } from "lucide-react";

import { usePermissions } from "@/hooks/usePermissions";

interface TrialExpiredLockoutProps {
  /** Plan key the org dropped into (usually "free"). */
  plan?: string | null;
}

/**
 * Routes that remain reachable even when the trial has expired. The
 * admin needs the Billing page to actually select a plan, plus the
 * top-level Settings hub so they can find it. Anything else is
 * replaced by the lockout view.
 */
const ALLOWED_PATH_PREFIXES = [
  "/settings/billing",
  "/settings", // landing hub — has the "Billing" link
  "/logout",
];

/**
 * Returns ``true`` when the current location should bypass the
 * lockout. Caller renders the normal Outlet in that case so the
 * admin can complete checkout.
 */
export function pathBypassesTrialLockout(pathname: string): boolean {
  return ALLOWED_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

export function TrialExpiredLockout({ plan }: TrialExpiredLockoutProps) {
  const { hasRole } = usePermissions();
  const location = useLocation();
  // SUPER_ADMIN is the buyer-of-record and the only role with
  // ``manage_billing`` — they see the upgrade CTA. Other roles see a
  // softer message pointing them at their admin.
  const canPurchase = hasRole("SUPER_ADMIN");

  return (
    <div
      role="alert"
      aria-live="polite"
      className="mx-auto flex w-full max-w-2xl flex-col items-center gap-6 rounded-2xl border border-border/50 bg-surface px-6 py-12 text-center shadow-sm"
      data-testid="trial-expired-lockout"
      data-pathname={location.pathname}
      data-plan={plan ?? ""}
    >
      <div className="rounded-full bg-warning/10 p-4 text-warning">
        <Lock className="h-8 w-8" />
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-text-primary">
          Your trial has ended
        </h1>
        <p className="max-w-md text-sm text-text-muted">
          {canPurchase
            ? "Pick a plan to unlock your workspace again. Your data is safe — nothing is deleted while you decide."
            : "Your workspace is paused until an administrator selects a plan. Your data is safe in the meantime."}
        </p>
      </div>

      {canPurchase ? (
        <div className="flex flex-col items-center gap-2">
          <Link
            to="/settings/billing"
            className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent/90"
          >
            <Sparkles className="h-4 w-4" />
            Choose a plan
          </Link>
          <span className="text-xs text-text-muted">
            Founding rates locked in for early customers.
          </span>
        </div>
      ) : (
        <p className="text-xs text-text-muted">
          Contact your workspace administrator to resume access.
        </p>
      )}
    </div>
  );
}
